import React, { useMemo, useRef } from 'react';
import './JsonCodeTextArea.css';

// Strings (with an optional trailing colon marking object keys), booleans,
// null, and numbers. Everything between matches is punctuation/whitespace.
const JSON_TOKEN_REGEX =
    /("(?:\\.|[^"\\])*")(\s*:)?|\b(?:true|false)\b|\bnull\b|-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

const tokenClass = (match) => {
    if (match[1] !== undefined) {
        return match[2] !== undefined ? 'json-code-textarea__key' : 'json-code-textarea__string';
    }
    if (match[0] === 'true' || match[0] === 'false' || match[0] === 'null') {
        return 'json-code-textarea__literal';
    }
    return 'json-code-textarea__number';
};

const highlightJson = (source) => {
    const nodes = [];
    let lastIndex = 0;
    let match;
    JSON_TOKEN_REGEX.lastIndex = 0;
    while ((match = JSON_TOKEN_REGEX.exec(source)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(source.slice(lastIndex, match.index));
        }
        if (match[1] !== undefined && match[2] !== undefined) {
            // Key string and its colon get separate colors.
            nodes.push(
                <span key={nodes.length} className="json-code-textarea__key">
                    {match[1]}
                </span>
            );
            nodes.push(match[2]);
        } else {
            nodes.push(
                <span key={nodes.length} className={tokenClass(match)}>
                    {match[0]}
                </span>
            );
        }
        lastIndex = match.index + match[0].length;
    }
    if (lastIndex < source.length) {
        nodes.push(source.slice(lastIndex));
    }
    return nodes;
};

/**
 * Textarea for JSON content with syntax-highlighted text. Renders the
 * standard Spark textarea chrome with a highlight layer underneath a
 * transparent-text textarea (same font/metrics), so editing behaves exactly
 * like a normal textarea while the visible text is colored.
 */
const JsonCodeTextArea = ({
    value,
    onChange,
    placeholder,
    rows = 5,
    disabled = false,
    style,
}) => {
    const highlightRef = useRef(null);
    const currentValue = value || '';
    const tokens = useMemo(() => highlightJson(currentValue), [currentValue]);

    const syncScroll = (e) => {
        if (highlightRef.current) {
            highlightRef.current.scrollTop = e.target.scrollTop;
            highlightRef.current.scrollLeft = e.target.scrollLeft;
        }
    };

    const rootClasses = [
        'spark-input-textarea',
        'spark-input-textarea-stretch',
        `spark-input-textarea-fill-${currentValue.length > 0 ? 'filled' : 'empty'}`,
        `spark-input-textarea-state-${disabled ? 'disabled' : 'default'}`,
        disabled ? 'spark-input-textarea-disabled' : '',
        'json-code-textarea',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <div className={rootClasses} style={{ maxWidth: '100%', ...style }}>
            <div className="spark-input-textarea-wrap">
                <div className="spark-input-textarea-box">
                    <pre ref={highlightRef} className="json-code-textarea__highlight" aria-hidden="true">
                        {tokens}
                        {'\n'}
                    </pre>
                    <textarea
                        className="spark-input-textarea-field json-code-textarea__field"
                        placeholder={placeholder}
                        value={currentValue}
                        onChange={onChange}
                        onScroll={syncScroll}
                        rows={rows}
                        disabled={disabled}
                        spellCheck={false}
                        autoCorrect="off"
                        autoCapitalize="off"
                    />
                </div>
            </div>
        </div>
    );
};

export default JsonCodeTextArea;
